import { useEffect, useRef } from 'react';

// Default idle window: 15 minutes. The VITE_IDLE_TIMEOUT_MS override exists only
// so E2E can use a short value — production leaves it unset.
const IDLE_TIMEOUT_MS = Number(import.meta.env.VITE_IDLE_TIMEOUT_MS) || 15 * 60 * 1000;

// Throttle activity resets: at most ~once per second so rapid mousemove/scroll
// events don't thrash the timer restart.
const RESET_THROTTLE_MS = 1000;

const ACTIVITY_EVENTS = [
  { type: 'mousemove', passive: true },
  { type: 'mousedown', passive: false },
  { type: 'keydown', passive: false },
  { type: 'scroll', passive: true },
  { type: 'touchstart', passive: true },
  { type: 'click', passive: false },
];

/**
 * Fires `onIdle` once after IDLE_TIMEOUT_MS of no user activity.
 *
 * Pure hook with no context dependencies (keeps it testable and reusable).
 * @param {object}   params
 * @param {boolean}  params.isActive  Gates whether the timer runs at all.
 * @param {Function} params.onIdle    Called once when the idle window elapses.
 */
export function useIdleTimeout({ isActive, onIdle }) {
  const timerRef = useRef(null);
  const lastActivityRef = useRef(0);
  const firedRef = useRef(false);
  // Keep onIdle in a ref so activity listeners don't re-subscribe every render.
  const onIdleRef = useRef(onIdle);

  useEffect(() => {
    onIdleRef.current = onIdle;
  }, [onIdle]);

  useEffect(() => {
    if (!isActive) return undefined;

    firedRef.current = false;
    lastActivityRef.current = Date.now();

    const handleIdle = () => {
      // Guard so onIdle fires at most once per idle period.
      if (firedRef.current) return;
      firedRef.current = true;
      onIdleRef.current?.();
    };

    const startTimer = () => {
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(handleIdle, IDLE_TIMEOUT_MS);
    };

    const handleActivity = () => {
      // Ignore activity after we've already fired — the consumer is logging out.
      if (firedRef.current) return;
      const now = Date.now();
      if (now - lastActivityRef.current < RESET_THROTTLE_MS) return;
      lastActivityRef.current = now;
      startTimer();
    };

    ACTIVITY_EVENTS.forEach(({ type, passive }) => {
      window.addEventListener(type, handleActivity, { passive });
    });
    startTimer();

    return () => {
      clearTimeout(timerRef.current);
      ACTIVITY_EVENTS.forEach(({ type, passive }) => {
        window.removeEventListener(type, handleActivity, { passive });
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- onIdle is read via onIdleRef; depending on it would restart the idle countdown on every identity change
  }, [isActive]);
}
